import * as THREE from 'three';
import { clamp, hash01, lerp, smoothstep } from '@/core/rng';

/**
 * Fire: flames, smoke and glow for burning cells. Render-only, like the clouds
 * and the birds — the burn itself lives in the simulation, and this module
 * only draws whatever cells it is handed each frame. It never reads or writes
 * Region or chunk state. Every flicker, jitter and puff derives from tSec and
 * the cell's own coordinates, so two machines watching the same fire see the
 * same fire.
 */
export interface FireFx {
  group: THREE.Group;
  /** cells arrive with ground y already resolved by the caller */
  update(tSec: number, dt: number,
         cells: { x: number; y: number; z: number; intensity: number }[],
         wind: { x: number; z: number }): void;
}

const FLAME_CAP = 1024;
const SMOKE_CAP = 700;
const TAU = Math.PI * 2;

/** Soft teardrop on a canvas: a hot core fading upward. Additive, so edges vanish. */
function flameTexture(): THREE.CanvasTexture {
  const w = 64, h = 128;
  const cv = document.createElement('canvas');
  cv.width = w; cv.height = h;
  const g = cv.getContext('2d')!;
  g.clearRect(0, 0, w, h);
  // broad body, stretched tall so the quad reads as a tongue and not a ball
  g.save();
  g.translate(w / 2, h * 0.80);
  g.scale(1, 2.6);
  let grad = g.createRadialGradient(0, 0, 0, 0, 0, w * 0.48);
  grad.addColorStop(0, 'rgba(255,244,214,0.9)');
  grad.addColorStop(0.45, 'rgba(255,224,170,0.42)');
  grad.addColorStop(1, 'rgba(255,190,110,0)');
  g.fillStyle = grad;
  g.fillRect(-w, -h, w * 2, h * 2);
  g.restore();
  // tight hot core near the base
  g.save();
  g.translate(w / 2, h * 0.84);
  g.scale(1, 1.7);
  grad = g.createRadialGradient(0, 0, 0, 0, 0, w * 0.22);
  grad.addColorStop(0, 'rgba(255,255,255,1)');
  grad.addColorStop(1, 'rgba(255,235,200,0)');
  g.fillStyle = grad;
  g.fillRect(-w, -h, w * 2, h * 2);
  g.restore();
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** Soft irregular blob — the clouds.ts trick, smaller and rounder. */
function smokeTexture(): THREE.CanvasTexture {
  const s = 128;
  const cv = document.createElement('canvas');
  cv.width = cv.height = s;
  const g = cv.getContext('2d')!;
  const rnd = (() => { let a = 5171; return () => ((a = (a * 1664525 + 1013904223) >>> 0) / 4294967296); })();
  g.clearRect(0, 0, s, s);
  for (let i = 0; i < 4; i++) {
    const x = s * (0.36 + rnd() * 0.28), y = s * (0.36 + rnd() * 0.28);
    const r = s * (0.22 + rnd() * 0.2);
    const grad = g.createRadialGradient(x, y, 0, x, y, r);
    grad.addColorStop(0, 'rgba(255,255,255,0.6)');
    grad.addColorStop(0.6, 'rgba(255,255,255,0.3)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grad;
    g.fillRect(0, 0, s, s);
  }
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** Two unit quads crossed at 90°, anchored at the base so y-scale stretches upward. */
function crossedPlanes(): THREE.BufferGeometry {
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array([
    -0.5, 0, 0,   0.5, 0, 0,   0.5, 1, 0,   -0.5, 1, 0,
     0, 0, -0.5,  0, 0, 0.5,   0, 1, 0.5,    0, 1, -0.5,
  ]), 3));
  geo.setAttribute('uv', new THREE.BufferAttribute(new Float32Array([
    0, 0, 1, 0, 1, 1, 0, 1,
    0, 0, 1, 0, 1, 1, 0, 1,
  ]), 2));
  geo.setIndex([0, 1, 2, 0, 2, 3, 4, 5, 6, 4, 6, 7]);
  return geo;
}

export function createFireFx(): FireFx {
  const group = new THREE.Group();

  // ---- flames: one instanced crossed-plane, rebuilt from scratch each frame
  const flames = new THREE.InstancedMesh(crossedPlanes(), new THREE.MeshBasicMaterial({
    map: flameTexture(), transparent: true, depthWrite: false,
    blending: THREE.AdditiveBlending, side: THREE.DoubleSide, fog: true,
  }), FLAME_CAP);
  flames.frustumCulled = false;
  flames.renderOrder = 1;
  flames.instanceMatrix.setUsage(THREE.DynamicDrawUsage);

  // ---- smoke: up-facing pancakes, like the clouds — the camera is mostly above
  const smokeGeo = new THREE.PlaneGeometry(1, 1);
  smokeGeo.rotateX(-Math.PI / 2);
  const smoke = new THREE.InstancedMesh(smokeGeo, new THREE.MeshBasicMaterial({
    map: smokeTexture(), transparent: true, opacity: 0.32, depthWrite: false,
    side: THREE.DoubleSide, fog: true,
  }), SMOKE_CAP);
  smoke.frustumCulled = false;
  smoke.renderOrder = 2;   // over the flames, so a column reads as dimming the blaze
  smoke.instanceMatrix.setUsage(THREE.DynamicDrawUsage);

  // allocate instanceColor up front — setColorAt creates it on first use
  const _c = new THREE.Color(1, 1, 1);
  for (let i = 0; i < FLAME_CAP; i++) flames.setColorAt(i, _c);
  for (let i = 0; i < SMOKE_CAP; i++) smoke.setColorAt(i, _c);
  flames.instanceColor!.setUsage(THREE.DynamicDrawUsage);
  smoke.instanceColor!.setUsage(THREE.DynamicDrawUsage);

  // ---- glow: one warm point light at the heart of the burn
  const light = new THREE.PointLight(0xff7733, 0, 900, 2);

  group.add(flames, smoke, light);

  // smoke pool — flat preallocated fields, packed [0, smokeCount)
  const smX = new Float32Array(SMOKE_CAP), smY = new Float32Array(SMOKE_CAP);
  const smZ = new Float32Array(SMOKE_CAP), smAge = new Float32Array(SMOKE_CAP);
  const smLife = new Float32Array(SMOKE_CAP), smRise = new Float32Array(SMOKE_CAP);
  const smSize = new Float32Array(SMOKE_CAP), smGrow = new Float32Array(SMOKE_CAP);
  const smWind = new Float32Array(SMOKE_CAP), smYaw = new Float32Array(SMOKE_CAP);
  let smokeCount = 0;
  // fractional spawn debt per cell slot, and a counter that seeds each puff —
  // deterministic given the caller's cell sequence, and no Math.random anywhere
  const spawnAcc = new Float32Array(FLAME_CAP);
  let prevN = 0;
  let seq = 1;

  // per-frame scratch — the update path allocates nothing
  const _m = new THREE.Matrix4();
  const _q = new THREE.Quaternion();
  const _p = new THREE.Vector3();
  const _s = new THREE.Vector3();
  const UP = new THREE.Vector3(0, 1, 0);

  return {
    group,
    update(tSec, dt, cells, wind) {
      const n = Math.min(cells.length, FLAME_CAP);

      // ---- flames: stateless, a pure function of the clock and the cell coords
      for (let i = 0; i < n; i++) {
        const cell = cells[i];
        const rx = Math.round(cell.x), rz = Math.round(cell.z);
        const k = clamp(cell.intensity, 0, 1);
        const ph = hash01(rx, rz, 67) * TAU;
        // stays well above zero — a flame that collapses between frames strobes
        const flick = 0.86 + 0.14 * Math.sin(tSec * 13 + ph)
                    + 0.06 * Math.sin(tSec * 7.1 + ph * 2.7);
        const tall = lerp(8, 20, k);   // big fires must read at god height
        _p.set(cell.x + (hash01(rx, rz, 61) - 0.5) * 3,
               cell.y - 0.6,           // rooted slightly into the ground
               cell.z + (hash01(rx, rz, 62) - 0.5) * 3);
        _q.setFromAxisAngle(UP, hash01(rx, rz, 64) * TAU);
        const wide = tall * 0.45 * (0.8 + hash01(rx, rz, 63) * 0.4);
        _s.set(wide, tall * flick, wide);
        _m.compose(_p, _q, _s);
        flames.setMatrixAt(i, _m);
        // yellow young burn shading into deep orange-red at full intensity
        _c.setRGB(lerp(1.0, 0.9, k), lerp(0.85, 0.25, k), lerp(0.35, 0.05, k));
        flames.setColorAt(i, _c);
      }
      flames.count = n;
      flames.instanceMatrix.needsUpdate = true;
      flames.instanceColor!.needsUpdate = true;

      // ---- smoke spawning: rate scales with fire size; positions are counter-hashed
      for (let i = 0; i < n; i++) {
        const cell = cells[i];
        const k = clamp(cell.intensity, 0, 1);
        spawnAcc[i] += (0.5 + k * 2.5) * dt;
        while (spawnAcc[i] >= 1) {
          spawnAcc[i] -= 1;
          if (smokeCount >= SMOKE_CAP) break;
          const j = smokeCount++;
          const s = seq++;
          const hk = Math.round(cell.x) * 31 + Math.round(cell.z);
          const ang = hash01(s, hk, 211) * TAU;
          const rad = 1.5 + hash01(s, hk, 213) * 5;
          smX[j] = cell.x + Math.cos(ang) * rad;
          smY[j] = cell.y + 3 + hash01(s, hk, 215) * 5;
          smZ[j] = cell.z + Math.sin(ang) * rad;
          smAge[j] = 0;
          smLife[j] = 14 + hash01(s, hk, 217) * 8;         // ~14–22 s
          smRise[j] = 2 + hash01(s, hk, 219) * 3;          // 2–5 m/s
          smSize[j] = 4.5 + hash01(s, hk, 221) * 4 + k * 8;
          smGrow[j] = 0.05 + hash01(s, hk, 223) * 0.05;
          smWind[j] = 0.55 + hash01(s, hk, 227) * 0.65;
          smYaw[j] = hash01(s, hk, 229) * TAU;
        }
      }
      // stale debt from slots the fire no longer fills must not burst on reignition
      for (let i = n; i < prevN; i++) spawnAcc[i] = 0;
      prevN = n;

      // ---- smoke aging: swap-remove keeps the pool packed; the dead cost nothing
      for (let i = 0; i < smokeCount; i++) {
        smAge[i] += dt;
        if (smAge[i] >= smLife[i]) {
          const l = --smokeCount;
          smX[i] = smX[l]; smY[i] = smY[l]; smZ[i] = smZ[l];
          smAge[i] = smAge[l]; smLife[i] = smLife[l]; smRise[i] = smRise[l];
          smSize[i] = smSize[l]; smGrow[i] = smGrow[l];
          smWind[i] = smWind[l]; smYaw[i] = smYaw[l];
          i--;
          continue;
        }
        smX[i] += wind.x * smWind[i] * dt;
        smZ[i] += wind.z * smWind[i] * dt;
        smY[i] += smRise[i] * dt;
      }
      for (let i = 0; i < smokeCount; i++) {
        const u = smAge[i] / smLife[i];
        // grow in fast, then fade by shrinking — opacity stays fixed and cheap
        const env = Math.min(1, smAge[i] / 1.6) * (1 - smoothstep(0.7, 1, u));
        const sz = smSize[i] * (1 + smAge[i] * smGrow[i]) * env;
        _p.set(smX[i], smY[i], smZ[i]);
        _q.setFromAxisAngle(UP, smYaw[i]);
        _s.set(sz, 1, sz);
        _m.compose(_p, _q, _s);
        smoke.setMatrixAt(i, _m);
        // slightly warm near birth — firelit — cooling to neutral grey
        const wt = clamp(1 - smAge[i] / 4, 0, 1);
        _c.setRGB(lerp(0.5, 0.6, wt), 0.5, lerp(0.52, 0.44, wt));
        smoke.setColorAt(i, _c);
      }
      smoke.count = smokeCount;
      smoke.instanceMatrix.needsUpdate = true;
      smoke.instanceColor!.needsUpdate = true;

      // ---- glow: what makes night fires glorious
      let wsum = 0, gx = 0, gy = 0, gz = 0;
      for (let i = 0; i < cells.length; i++) {
        const cell = cells[i];
        const w = Math.max(0.02, cell.intensity);
        wsum += w; gx += cell.x * w; gy += cell.y * w; gz += cell.z * w;
      }
      if (cells.length > 0) {
        light.position.set(gx / wsum, gy / wsum + 25, gz / wsum);
        const flick = 1 + 0.07 * Math.sin(tSec * 8.3) + 0.05 * Math.sin(tSec * 23.7);
        light.intensity = Math.min(6, cells.length * 0.15) * flick;
      } else {
        light.intensity = 0;
      }
    },
  };
}
